import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { PageShell, WarningCard } from "@/components/page-shell";
import {
  ArticleBody,
  DISEASE_CATEGORY_LABEL,
  DotList,
  EndNote,
  MoreLinks,
  Prose,
  ReviewedDate,
  Section,
  SourcesAndDisclaimer,
} from "@/components/guide-page";
import { InlineLinks } from "@/components/checklist";
import type { TocItem } from "@/components/toc";
import { Faq, faqJsonLd } from "@/components/faq";
import { SourceList } from "@/components/source-list";
import { ShareCard } from "@/components/share-card";
// Value import used only by the component below, so the route splitter keeps it in
// the component chunk; the loader imports the module dynamically for the same reason.
import { getDisease, type Disease } from "@/lib/diseases";
import { getSpecies } from "@/lib/species";
import { breadcrumbJsonLd, medicalWebPageJsonLd, articleJsonLd } from "@/lib/jsonld";
import { DISEASE_SAME_AS, conditionEntity } from "@/lib/entities";
import { absUrl, ogImageUrl } from "@/lib/site";
import { pageTitle } from "@/lib/seo";

export const Route = createFileRoute("/diseases/$disease")({
  // The content module is imported dynamically so it lands in this route's own chunk
  // instead of the shared entry chunk that every page downloads. routeTree.gen.ts
  // imports each route's loader and head eagerly, so a top-level import here would
  // put this file's prose on the home page's critical path.
  loader: async ({ params }): Promise<{ disease: Disease }> => {
    const { getDisease: lookup } = await import("@/lib/diseases");
    const disease = lookup(params.disease);
    if (!disease) throw notFound();
    // A condition with its own flagship route (e.g. alpha-gal) redirects there so the
    // two pages never compete for the same query.
    if (disease.canonicalPath) throw redirect({ href: disease.canonicalPath, code: 301 });
    return { disease };
  },
  head: ({ loaderData }) => {
    const d = loaderData?.disease;
    if (!d) return {};
    const path = `/diseases/${d.slug}`;
    const ogImage = ogImageUrl({
      title: d.h1,
      subtitle: `Tick-borne ${d.category === "allergic" ? "condition" : d.category + " illness"}`,
      kind: "guide",
    });
    return {
      meta: [
        { title: pageTitle(d.metaTitle) },
        { name: "description", content: d.metaDescription },
        { name: "keywords", content: d.keywords.join(", ") },
        { property: "og:title", content: d.h1 },
        { property: "og:description", content: d.metaDescription },
        { property: "og:url", content: absUrl(path) },
        { property: "og:type", content: "article" },
        { property: "og:image", content: ogImage },
        { name: "twitter:title", content: d.h1 },
        { name: "twitter:description", content: d.metaDescription },
        { name: "twitter:image", content: ogImage },
      ],
      links: [{ rel: "canonical", href: absUrl(path) }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            medicalWebPageJsonLd({
              name: d.h1,
              description: d.metaDescription,
              path,
              lastReviewed: d.lastReviewed,
              image: ogImage,
              about: [
                conditionEntity({
                  name: d.name,
                  alternateName: d.aliases,
                  sameAs: DISEASE_SAME_AS[d.slug],
                }),
              ],
            }),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            articleJsonLd({
              headline: d.h1,
              description: d.metaDescription,
              path,
              datePublished: d.datePublished,
              dateModified: d.lastReviewed,
              section: "Tick-borne diseases",
              keywords: d.keywords,
              image: ogImage,
            }),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(faqJsonLd(d.faq)) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbJsonLd([
              { name: "Home", path: "/" },
              { name: "Tick-borne diseases", path: "/diseases" },
              { name: d.name, path },
            ]),
          ),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <PageShell title="Disease not found">
      <p className="text-sm text-muted-foreground">
        We don't have a page for that condition yet.{" "}
        <Link to="/diseases" className="link font-semibold">
          Browse tick-borne diseases
        </Link>
        .
      </p>
    </PageShell>
  ),
  component: DiseasePage,
});

function DiseasePage() {
  const { disease: d } = Route.useLoaderData() as { disease: Disease };
  const relatedSpecies = d.relatedSpeciesSlugs.map(getSpecies).filter((x) => x !== undefined);
  const relatedDiseases = d.relatedDiseaseSlugs.map(getDisease).filter((x) => x !== undefined);
  const category = DISEASE_CATEGORY_LABEL[d.category];

  const sections: Array<{ id: string; h: string; body: string }> = [
    { id: "onset", h: "How soon symptoms appear", body: d.onsetWindow },
    { id: "diagnosis", h: "Diagnosis", body: d.diagnosis },
    { id: "treatment", h: "Treatment", body: d.treatment },
    { id: "prevention", h: "Prevention", body: d.prevention },
    { id: "where", h: "Where it occurs", body: d.geography },
  ];
  const toc: TocItem[] = [
    { id: "what-it-is", label: "What it is" },
    { id: "symptoms", label: "Symptoms" },
    ...sections.map((s) => ({ id: s.id, label: s.h })),
    { id: "seek-care", label: "When to seek care" },
    { id: "common-questions", label: "Common questions" },
    { id: "sources", label: "Sources" },
  ];

  return (
    <PageShell
      crumbs={[
        { name: "Home", path: "/" },
        { name: "Tick-borne diseases", path: "/diseases" },
        { name: d.name, path: `/diseases/${d.slug}` },
      ]}
      eyebrow={
        category ? `${category} ${d.category === "allergic" ? "condition" : "illness"}` : undefined
      }
      title={d.h1}
      intro={d.intro}
    >
      <ReviewedDate iso={d.lastReviewed} />

      <ArticleBody toc={toc}>
        {/* Which tick: the first thing a reader bitten by a Lone Star tick needs to know. */}
        <div className="measure border-l-[3px] border-rust py-1 pl-4 text-[0.9375rem] leading-relaxed text-foreground">
          {d.transmittedByLoneStar ? (
            <>
              <span className="font-semibold">Carried by the Lone Star tick.</span> Spread by:{" "}
              {d.vectorTicks.join(", ")}.
            </>
          ) : (
            <>
              <span className="font-semibold">Not transmitted by the Lone Star tick.</span> This
              illness is spread by a different tick: {d.vectorTicks.join(", ")}. If a Lone Star tick
              bit you, this is generally not the concern.
            </>
          )}
        </div>

        <Section id="what-it-is" title="What it is">
          <Prose>{d.whatItIs}</Prose>
        </Section>

        <Section id="symptoms" title="Symptoms">
          <DotList items={d.symptoms} columns={d.symptoms.every((s) => s.length < 48)} />
        </Section>

        {sections.map((s) => (
          <Section key={s.id} id={s.id} title={s.h}>
            <Prose>{s.body}</Prose>
          </Section>
        ))}

        <section aria-labelledby="seek-care">
          <WarningCard>
            <h2 id="seek-care" className="label-caps text-foreground">
              When to seek care
            </h2>
            <p className="measure mt-1.5 text-[0.9375rem]">
              {d.whenToSeekCare} For emergencies such as trouble breathing or anaphylaxis, call
              emergency services.
            </p>
          </WarningCard>
        </section>

        <Section id="common-questions" title="Common questions">
          <Faq items={d.faq} />
        </Section>

        <div id="sources">
          <SourceList ids={d.sourceIds} />
        </div>

        {(relatedSpecies.length > 0 || relatedDiseases.length > 0) && (
          <div className="space-y-6">
            {relatedSpecies.length > 0 && (
              <MoreLinks id="related-ticks" title="The ticks">
                {relatedSpecies.map((s) => (
                  <li key={s.slug}>
                    <Link to="/ticks/$species" params={{ species: s.slug }} className="link">
                      {s.commonName}
                    </Link>
                  </li>
                ))}
              </MoreLinks>
            )}
            {relatedDiseases.length > 0 && (
              <MoreLinks id="related-diseases" title="Related illnesses">
                {relatedDiseases.map((rd) => (
                  <li key={rd.slug}>
                    {/* Conditions with a flagship page (alpha-gal) link straight there, no redirect hop. */}
                    {rd.canonicalPath ? (
                      <Link to={rd.canonicalPath} className="link">
                        {rd.name}
                      </Link>
                    ) : (
                      <Link to="/diseases/$disease" params={{ disease: rd.slug }} className="link">
                        {rd.name}
                      </Link>
                    )}
                  </li>
                ))}
              </MoreLinks>
            )}
            <InlineLinks className="text-sm">
              <li>
                <Link to="/diseases" className="link font-semibold">
                  All tick-borne diseases
                </Link>
              </li>
              <li>
                <Link to="/tick-bite-tool" className="link">
                  What to do after a bite
                </Link>
              </li>
              <li>
                <Link to="/prevention" className="link">
                  How to avoid bites
                </Link>
              </li>
            </InlineLinks>
          </div>
        )}

        <ShareCard
          title={d.h1}
          subtitle={d.metaDescription}
          kind="guide"
          summaryText={`${d.h1}\n\n${d.intro}\n\nRead more: https://lonestarmap.com/diseases/${d.slug}`}
          sharePath={`/diseases/${d.slug}`}
          shareText={`${d.h1}: a free, sourced explainer from LoneStarMap:`}
        />

        <EndNote>
          This page is here to help you learn. It is not medical advice. Only a doctor can diagnose
          a tick-borne illness. <SourcesAndDisclaimer />
        </EndNote>
      </ArticleBody>
    </PageShell>
  );
}
